import {
  Button,
  Card,
  CardBody,
  Checkbox,
  ErrorState,
  Field,
  FormGrid,
  FormGridItem,
  Inline,
  InlineMessage,
  Input,
  OptionSelect,
  PageContent,
  PageHeader,
  Spinner,
  Stack,
  type StatusStep,
  StatusTimeline,
  Text,
  Textarea,
  TextLink,
  Wizard,
  type WizardStep,
} from '@olytic/foundation';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { authenticatedFetch } from '../auth/authClient';
import { useDocumentTitle } from '../documentTitle';
import { humanize } from '../objects/format';
import { listPath, recordPath } from '../objects/routes';
import {
  checkBasics,
  checkComponents,
  checkDelivery,
  checkPrices,
  createProduct,
  defaultPrice,
  type DraftPrice,
  EMPTY_DRAFT,
  needsEstimate,
  loadSetupOptions,
  newPrice,
  previewReadiness,
  PRICE_SCHEMES,
  PRODUCT_TYPES,
  type ProductDraft,
  ProductSetupError,
  type Readiness,
  type ReadinessItem,
  readinessSteps,
  roleTotal,
  SENIORITIES,
  SIZE_HOURS,
  SIZES,
  type SetupOptions,
  setupBody,
  STEP_FOR_FIX,
  type StepKey,
  stepKeys,
  typeForFamily,
} from './productSetup';

const SCHEME_LABELS: Record<string, string> = {
  flat: 'Flat: an amount per unit',
  per_hour: 'Per hour: an amount per hour',
  grid: 'Price Grid: a fixed menu by Company Size',
  included: 'Included at no charge',
};

const STEP_TEXT: Record<StepKey, { label: string; description: string }> = {
  basics: { label: 'Basics', description: 'What it is: its code, name, group and internal estimate.' },
  prices: { label: 'Prices', description: 'How it is sold, from today: a price per selling model (once, for a term, or until cancelled). One is the default.' },
  components: { label: 'Components', description: 'The products this bundle is made of.' },
  billing: { label: 'Billing', description: 'When it is invoiced: its billing policy, whose default treatment a quote starts from.' },
  delivery: { label: 'Delivery roles', description: 'Who staffs it: each role’s share of its hours, totalling 100%.' },
  review: { label: 'Review', description: 'What it still needs, by the same rules that let it be activated.' },
};

type Preview = { state: 'idle' } | { state: 'loading' } | { state: 'ready'; readiness: Readiness } | { state: 'error'; message: string };

const messageOf = (error: unknown, fallback: string): string =>
  error instanceof Error && error.message.length > 0 ? error.message : fallback;

/** A checkbox with its label beside it. */
function CheckField({ checked, onChange, label }: { checked: boolean; onChange: (checked: boolean) => void; label: string }): JSX.Element {
  return (
    <Inline as="label" gap="sm" align="center">
      <Checkbox checked={checked} onCheckedChange={(value) => onChange(value === true)} />
      <Text as="span">{label}</Text>
    </Inline>
  );
}

/**
 * The new-product flow at /products/new: basics, then only the steps this
 * kind of product needs (prices, components, billing,
 * delivery roles), then a review of what ops.product_readiness() says is
 * still missing. Saving writes everything in one transaction, as a draft or
 * active; activating is refused while anything blocks.
 */
export function NewProductPage(): JSX.Element {
  useDocumentTitle('New product');
  const navigate = useNavigate();
  const [options, setOptions] = useState<SetupOptions | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [draft, setDraft] = useState<ProductDraft>(EMPTY_DRAFT);
  const update = (patch: Partial<ProductDraft>) => setDraft((current) => ({ ...current, ...patch }));
  const [index, setIndex] = useState(0);
  const [preview, setPreview] = useState<Preview>({ state: 'idle' });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setLoadError(null);
    loadSetupOptions(authenticatedFetch, controller.signal)
      .then((o) => { if (!controller.signal.aborted) setOptions(o); })
      .catch((e: unknown) => { if (!controller.signal.aborted) setLoadError(messageOf(e, 'Could not load what a product is made from.')); });
    return () => controller.abort();
  }, [attempt]);

  const opts = options ?? { groups: [], selling_models: [], billing_policies: [], price_grids: [], resource_roles: [], products: [] };
  const keys = useMemo(() => stepKeys(draft, opts), [draft, opts]);

  function runPreview() {
    setPreview({ state: 'loading' });
    setSaveError(null);
    previewReadiness(authenticatedFetch, setupBody(draft, opts, false))
      .then((readiness) => setPreview({ state: 'ready', readiness }))
      .catch((e: unknown) => setPreview({ state: 'error', message: messageOf(e, 'Could not check the product.') }));
  }

  function goTo(next: number) {
    setIndex(next);
    if (keys[next] === 'review') runPreview();
  }

  async function save(activate: boolean) {
    setSaving(true);
    setSaveError(null);
    try {
      const { id } = await createProduct(authenticatedFetch, setupBody(draft, opts, activate));
      navigate(recordPath('products', id));
    } catch (error) {
      setSaveError(messageOf(error, 'The product could not be saved.'));
      if (error instanceof ProductSetupError && error.readiness) setPreview({ state: 'ready', readiness: error.readiness });
      setSaving(false);
    }
  }

  if (loadError) {
    return (
      <PageContent as="section" width="default">
        <ErrorState title="Could not start a new product" description={loadError} onRetry={() => setAttempt((n) => n + 1)} />
      </PageContent>
    );
  }
  if (!options) {
    return (
      <PageContent as="section" width="default">
        <Spinner label="Loading…" />
      </PageContent>
    );
  }

  const groupOptions = options.groups.map((g) => ({ value: g.id, label: `${g.name} (${humanize(g.family)})` }));
  const policy = options.billing_policies.find((p) => p.id === draft.billing_policy_id);
  const total = roleTotal(draft);
  const estimate = needsEstimate(draft, options);
  const monthly = options.selling_models.some((m) => m.id === defaultPrice(draft)?.selling_model_id && m.kind !== 'one_time');
  const setPrice = (i: number, patch: Partial<DraftPrice>) =>
    update({
      prices: draft.prices.map((p, j) => {
        if (patch.is_default === true) return j === i ? { ...p, ...patch } : { ...p, is_default: false };
        return j === i ? { ...p, ...patch } : p;
      }),
    });
  const modelName = (id: number | null | undefined) => options.selling_models.find((m) => m.id === id)?.name ?? '';
  const ready = preview.state === 'ready' && preview.readiness.ready;

  const content: Record<StepKey, JSX.Element> = {
    basics: (
      <FormGrid columns={2}>
        <Field label="Name" required>
          <Input value={draft.name} maxLength={200} onChange={(e) => update({ name: e.target.value })} />
        </Field>
        <Field label="Code" required>
          <Input value={draft.code} maxLength={50} onChange={(e) => update({ code: e.target.value })} />
        </Field>
        <Field label="Product Group" required>
          <OptionSelect options={groupOptions} value={draft.group_id} placeholder="Choose a group" onValueChange={(group_id) => update({ group_id, type: typeForFamily(options.groups.find((g) => g.id === group_id)?.family) })} />
        </Field>
        <Field label="Type" help="Defaults from its group.">
          <OptionSelect
            options={PRODUCT_TYPES.map((t) => ({ value: t, label: humanize(t) }))}
            value={draft.type}
            placeholder="Choose a group first"
            onValueChange={(type) => update({ type: PRODUCT_TYPES.find((t) => t === type) ?? '' })}
          />
        </Field>
        <Field label="Structure">
          <OptionSelect
            options={[{ value: 'single', label: 'Single' }, { value: 'bundle', label: 'Bundle: made of components' }]}
            value={draft.structure}
            onValueChange={(structure) => update({ structure: structure === 'bundle' ? 'bundle' : 'single' })}
          />
        </Field>
        <Field
          label="Estimated Hours"
          required
          help={monthly ? 'Internal: hours a month, 0 when it takes none. Never shown to customers.' : 'Internal: 0 when it takes none. Never shown to customers.'}
        >
          <Input type="number" min={0} step="0.5" value={draft.estimated_hours} onChange={(e) => update({ estimated_hours: e.target.value })} />
        </Field>
        {estimate ? (
          <Field label="Size" required help={draft.size ? `Internal. ${draft.size} is usually ${SIZE_HOURS[draft.size as keyof typeof SIZE_HOURS]}.` : 'Internal: XS to XL.'}>
            <OptionSelect options={SIZES.map((s) => ({ value: s, label: `${s} (${SIZE_HOURS[s]})` }))} value={draft.size} placeholder="Choose" onValueChange={(size) => update({ size })} />
          </Field>
        ) : null}
        {estimate ? (
          <Field label="Seniority" help="Internal: who usually does the work.">
            <OptionSelect options={SENIORITIES.map((s) => ({ value: s, label: humanize(s) }))} value={draft.seniority} placeholder="Choose" onValueChange={(seniority) => update({ seniority })} />
          </Field>
        ) : null}
        <FormGridItem span="full">
          <CheckField
            checked={draft.is_component}
            onChange={(is_component) => update({ is_component, ...(is_component ? { structure: 'single' as const, billing_policy_id: '' } : {}) })}
            label="Component: sold only inside a bundle, never billed on its own"
          />
        </FormGridItem>
        <FormGridItem span="full">
          <Field label="Description" help="Customer-facing: shown on quotes and proposals.">
            <Textarea rows={4} value={draft.description} onChange={(e) => update({ description: e.target.value })} />
          </Field>
        </FormGridItem>
      </FormGrid>
    ),
    prices: (
      <Stack gap="md">
        {draft.prices.map((price, i) => (
          <Card key={i}>
            <CardBody>
              <FormGrid columns={2}>
                <Field label="Selling Model" required>
                  <OptionSelect
                    options={options.selling_models.map((m) => ({ value: String(m.id), label: m.name }))}
                    value={price.selling_model_id === null ? '' : String(price.selling_model_id)}
                    placeholder="Choose how it is sold"
                    onValueChange={(value) => setPrice(i, { selling_model_id: value === '' ? null : Number(value) })}
                  />
                </Field>
                <Field label="Scheme">
                  <OptionSelect
                    options={PRICE_SCHEMES.map((scheme) => ({ value: scheme, label: SCHEME_LABELS[scheme] ?? humanize(scheme) }))}
                    value={price.scheme}
                    onValueChange={(scheme) => setPrice(i, { scheme: PRICE_SCHEMES.find((x) => x === scheme) ?? 'flat' })}
                  />
                </Field>
                {options.selling_models.find((m) => m.id === price.selling_model_id && m.kind !== 'one_time') ? (
                  <Field label="Period" help="What the amount is per: a term line's total is the amount × its term ÷ this.">
                    <OptionSelect
                      options={[{ value: 'month', label: 'Per Month' }, { value: 'year', label: 'Per Year' }]}
                      value={price.period}
                      onValueChange={(period) => setPrice(i, { period: period === 'year' ? 'year' : 'month' })}
                    />
                  </Field>
                ) : null}
                {price.scheme === 'flat' || price.scheme === 'per_hour' ? (
                  <Field label="Amount" required help={price.scheme === 'per_hour' ? 'Per hour.' : 'Per unit, per period for a recurring selling model.'}>
                    <Input type="number" min={0} step="0.01" startSlot="$" value={price.amount} onChange={(e) => setPrice(i, { amount: e.target.value })} />
                  </Field>
                ) : null}
                {price.scheme === 'grid' ? (
                  <Field label="Price Grid" required help="Its cells hold the amounts and hours.">
                    <OptionSelect
                      options={options.price_grids.map((g) => ({ value: g.id, label: g.name }))}
                      value={price.price_grid_id}
                      placeholder={options.price_grids.length ? 'Choose a grid' : 'No Price Grids yet'}
                      onValueChange={(price_grid_id) => setPrice(i, { price_grid_id })}
                    />
                  </Field>
                ) : null}
                <Field label="Label" help={`What a quote calls it, if not its selling model${price.selling_model_id ? ` (${modelName(price.selling_model_id)})` : ''}.`}>
                  <Input value={price.label} maxLength={200} onChange={(e) => setPrice(i, { label: e.target.value })} />
                </Field>
                <FormGridItem span="full">
                  <Inline gap="md" align="center">
                    <CheckField checked={price.is_default} onChange={(on) => on && setPrice(i, { is_default: true })} label="Default: what a quote starts from" />
                    {draft.prices.length > 1 ? (
                      <Button
                        variant="subtle"
                        onClick={() => {
                          const rest = draft.prices.filter((_, j) => j !== i);
                          update({ prices: price.is_default && rest[0] ? [{ ...rest[0], is_default: true }, ...rest.slice(1)] : rest });
                        }}
                      >
                        Remove
                      </Button>
                    ) : null}
                  </Inline>
                </FormGridItem>
              </FormGrid>
            </CardBody>
          </Card>
        ))}
        <Inline>
          <Button variant="neutral" onClick={() => update({ prices: [...draft.prices, newPrice(draft.prices.length === 0)] })}>
            Add a price
          </Button>
        </Inline>
      </Stack>
    ),
    components: (
      <Stack gap="md">
        {draft.options.length === 0 ? <Text tone="muted">No components yet. A bundle needs at least one before it can be activated.</Text> : null}
        {draft.options.map((option, i) => {
          const set = (patch: Partial<typeof option>) =>
            update({ options: draft.options.map((o, j) => (j === i ? { ...o, ...patch } : o)) });
          return (
            <Card key={i}>
              <CardBody>
                <FormGrid columns={2}>
                  <Field label="Product" required>
                    <OptionSelect
                      options={options.products.map((p) => ({ value: p.id, label: `${p.code} · ${p.name}${p.is_component ? '' : ' (not a component)'}` }))}
                      value={option.option_product_id}
                      placeholder="Choose a product"
                      onValueChange={(option_product_id) => set({ option_product_id })}
                    />
                  </Field>
                  <Field label="Group" help="The heading it sits under when configuring.">
                    <Input value={option.option_group} maxLength={100} onChange={(e) => set({ option_group: e.target.value })} />
                  </Field>
                  <Field label="Default Quantity" help="Added with the bundle at this quantity; blank: offered, not added.">
                    <Input type="number" min={0} step="1" value={option.default_quantity} onChange={(e) => set({ default_quantity: e.target.value })} />
                  </Field>
                  <Stack gap="sm">
                    <CheckField checked={option.required} onChange={(required) => set({ required })} label="Required" />
                    <CheckField checked={option.included} onChange={(included) => set({ included })} label="Included at No Charge" />
                  </Stack>
                  <FormGridItem span="full">
                    <Button variant="subtle" onClick={() => update({ options: draft.options.filter((_, j) => j !== i) })}>
                      Remove
                    </Button>
                  </FormGridItem>
                </FormGrid>
              </CardBody>
            </Card>
          );
        })}
        <Inline>
          <Button
            variant="neutral"
            onClick={() => update({ options: [...draft.options, { option_product_id: '', option_group: '', required: false, included: false, default_quantity: '' }] })}
          >
            Add component
          </Button>
        </Inline>
      </Stack>
    ),
    billing: (
      <FormGrid columns={2}>
        <Field
          label="Billing Policy"
          help={policy ? `${policy.description ?? policy.name}${policy.default_treatment ? ` Default: ${policy.default_treatment}.` : ' It has no default treatment yet.'}` : 'Required before it can be activated.'}
        >
          <OptionSelect
            options={options.billing_policies.map((p) => ({ value: p.id, label: p.name }))}
            value={draft.billing_policy_id}
            emptyLabel="Choose later"
            onValueChange={(billing_policy_id) => update({ billing_policy_id })}
          />
        </Field>
        <Field label="QuickBooks Item" help="The item its invoice lines map to. Can be added later.">
          <Input value={draft.qbo_item_id} maxLength={100} onChange={(e) => update({ qbo_item_id: e.target.value })} />
        </Field>
      </FormGrid>
    ),
    delivery: (
      <Stack gap="md">
        <FormGrid columns={2}>
          {options.resource_roles.map((role) => (
            <Field key={role.id} label={role.name} {...(role.description ? { help: role.description } : {})}>
              <Input
                type="number"
                min={0}
                max={100}
                step="0.01"
                endSlot="%"
                value={draft.roles[role.id] ?? ''}
                onChange={(e) => update({ roles: { ...draft.roles, [role.id]: e.target.value } })}
              />
            </Field>
          ))}
        </FormGrid>
        <Text tone="muted" role="status">Total: {total}% of its hours{total === 100 ? '.' : ', of 100%.'}</Text>
      </Stack>
    ),
    review: (
      <Stack gap="md">
        <Text>
          <strong>{draft.code || 'No code'}</strong> · {draft.name || 'No name'}: {draft.is_component ? 'a component' : humanize(draft.structure).toLowerCase()},{' '}
          {draft.prices.length === 1 ? 'one price' : `${draft.prices.length} prices`}
          {defaultPrice(draft)?.selling_model_id ? `, ${modelName(defaultPrice(draft)?.selling_model_id).toLowerCase()} by default` : ''}.
        </Text>
        {preview.state === 'loading' || preview.state === 'idle' ? <Spinner label="Checking what it still needs…" /> : null}
        {preview.state === 'error' ? <InlineMessage tone="error" role="alert">{preview.message}</InlineMessage> : null}
        {preview.state === 'ready' ? (
          <StatusTimeline
            steps={readinessSteps(preview.readiness, (item: ReadinessItem): StatusStep['detail'] => {
              const step = item.fix ? STEP_FOR_FIX[item.fix] : undefined;
              const at = step ? keys.indexOf(step) : -1;
              if (at < 0) return undefined;
              return (
                <TextLink href={`#${step}`} onClick={(event) => { event.preventDefault(); goTo(at); }}>
                  Back to {STEP_TEXT[step as StepKey].label}
                </TextLink>
              );
            })}
          />
        ) : null}
        {saveError ? <InlineMessage tone="error" role="alert">{saveError}</InlineMessage> : null}
        <Inline gap="sm" align="center" wrap>
          {ready ? (
            <>
              <Text tone="muted">Nothing blocks it: save it active and it can be quoted at once, or keep it as a draft.</Text>
              <Button variant="neutral" disabled={saving} onClick={() => void save(false)}>Save as draft</Button>
            </>
          ) : (
            <>
              <Text tone="muted">It can be saved as a draft now and finished on its page; it is not quoted until activated.</Text>
              <Button variant="neutral" disabled>Save and activate</Button>
            </>
          )}
        </Inline>
      </Stack>
    ),
  };

  const validators: Partial<Record<StepKey, () => string | null>> = {
    basics: () => checkBasics(draft),
    prices: () => checkPrices(draft),
    components: () => checkComponents(draft),
    delivery: () => checkDelivery(draft),
  };

  const steps: WizardStep[] = keys.map((key) => {
    const validate = validators[key];
    return {
      key,
      label: STEP_TEXT[key].label,
      description: STEP_TEXT[key].description,
      content: content[key],
      ...(validate ? { validate } : {}),
    };
  });

  return (
    <PageContent as="section" width="default">
      <Stack>
        <PageHeader className="border-0 bg-transparent px-0 pt-0 pb-0" eyebrow="Products" title="New product" />
        <Card>
          <CardBody>
            <Wizard
              steps={steps}
              index={Math.min(index, steps.length - 1)}
              onIndexChange={goTo}
              onComplete={() => void save(ready)}
              submitLabel={ready ? 'Save and activate' : 'Save as draft'}
              onCancel={() => navigate(listPath('products'))}
              busy={saving}
            />
          </CardBody>
        </Card>
      </Stack>
    </PageContent>
  );
}
